/**
 * LES FORMATEURS DE LA LANGUE, FABRIQUES UNE FOIS.
 *
 * `valeur.toLocaleString('fr-FR', { ... })`, `a.localeCompare(b, 'fr', { ... })`
 * et `new Intl.DateTimeFormat(...)` construisent un formateur NEUF a chaque
 * appel : quelques microsecondes, mais la projection d'un ecran les appelle une
 * fois par cellule, et le tri une fois par comparaison. Sur 5 000 fiches, le
 * seul habillage « taux » coutait 600 ms par frappe dans la recherche, et un
 * tri alphabetique 90 ms a 1 500 fiches contre 4 ms avec un comparateur garde
 * (profil et mesures du chantier `ecran-fluide`, `docs/socle/ecran-fluide.md`).
 *
 * Le resultat est EXACTEMENT celui de l'appel d'origine : memes options, meme
 * langue. Seule la fabrication est mise en cache, par la clef de ses options.
 */

const NOMBRES = new Map<string, Intl.NumberFormat>();
const DATES = new Map<string, Intl.DateTimeFormat>();
const COLLATEURS = new Map<string, Intl.Collator>();

function clefDe(langue: string | undefined, options: object | undefined): string {
  return `${langue ?? ''}|${options === undefined ? '' : JSON.stringify(options)}`;
}

/** Le formateur de nombres de ces options (`toLocaleString` d'un nombre, sans le recreer). */
export function formatDeNombre(langue: string | undefined, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  const clef = clefDe(langue, options);
  let format = NOMBRES.get(clef);
  if (format === undefined) {
    format = new Intl.NumberFormat(langue, options);
    NOMBRES.set(clef, format);
  }
  return format;
}

/** `valeur.toLocaleString(langue, options)`, avec un formateur garde. */
export function nombreEnTexte(valeur: number, langue: string | undefined, options?: Intl.NumberFormatOptions): string {
  return formatDeNombre(langue, options).format(valeur);
}

/** Le formateur de dates de ces options (fuseau compris), sans le recreer. */
export function formatDeDate(langue: string | undefined, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const clef = clefDe(langue, options);
  let format = DATES.get(clef);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(langue, options);
    DATES.set(clef, format);
  }
  return format;
}

/** Le comparateur de textes de ces options (`localeCompare`, sans le recreer). */
export function collateur(langue: string | undefined, options?: Intl.CollatorOptions): Intl.Collator {
  const clef = clefDe(langue, options);
  let format = COLLATEURS.get(clef);
  if (format === undefined) {
    format = new Intl.Collator(langue, options);
    COLLATEURS.set(clef, format);
  }
  return format;
}
